import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './i18n'
import App from './App'
import { initObservability, captureError } from './services/observability'
import { initSentry, SentryObservability } from './services/observability/sentry-impl'
import { container } from './services/container'
import { SupabaseAuthProvider } from './services/providers/supabase-auth/SupabaseAuthProvider'
import { setUnauthorizedHandler, setUserTokenProvider } from './services/providers/supabase/api'
import { wireNativeAutoRefresh } from './services/auth/nativeAutoRefresh'

// Wire api.ts's 401/403 broadcast through IAuthProvider.notifyUnauthorized.
// Replaces the pre-Phase-1a window.dispatchEvent('shopman:unauthorized')
// path that violated the service-layer invariant rules.
setUnauthorizedHandler((listName) => container.auth?.notifyUnauthorized(listName))

// Phase A: wire the user Bearer token into api.ts so all edge function calls
// carry the authenticated user's identity. getFreshUserToken() refreshes the
// access token first if it has expired (native/Capacitor background-resume),
// so the server never sees a stale "Invalid or expired user token". Returns
// null when not signed in, falling back to the anon key.
setUserTokenProvider(() => container.auth?.getFreshUserToken() ?? Promise.resolve(null))

// Native (Capacitor) background token-refresh: keep supabase-js' 30s refresh
// timer in sync with the app's foreground/background state so the access_token
// (and the realtime channel auth) does not silently expire while backgrounded.
// No-op on web - the browser keeps the SDK timer alive. See
// TODO.md#stale-user-token-native.
if (container.auth instanceof SupabaseAuthProvider) {
  wireNativeAutoRefresh(container.auth.getClient())
}

// Phase 5 (autonomous PLAN id 5; roadmap Phase 3) — Sentry/Logflare wiring.
// @sentry/react is installed and wired BEHIND the VITE_SENTRY_DSN build-time
// flag: with a DSN set the real Sentry impl initializes; without one (dev,
// preview, or before TJK provisions the project) the console-based no-op impl
// stays active so dev consoles are not polluted. Activating Sentry in prod is
// purely a matter of setting VITE_SENTRY_DSN at build time — no code change.
// Edge-function-side Sentry (Deno SDK) is still TODO.md#phase-5-sentry-install.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
if (SENTRY_DSN) {
  initSentry(SENTRY_DSN)
  initObservability(new SentryObservability())
} else {
  initObservability({
    captureError: (err, ctx) => console.error('[obs.error]', err, ctx ?? {}),
    captureMessage: (msg, ctx) => console.log('[obs.msg]', msg, ctx ?? {}),
    addBreadcrumb: (cat, msg, data) => {
      void cat; void msg; void data;
    },
  })
}

// Register service worker + auto-update flow.
//
// Hvordan PWA'en opdaterer sig selv (også installeret på hjemmeskærmen):
//   1. sw.js stemples med en build-version (scripts/stamp-sw.mjs), så dens
//      bytes ændrer sig når assets ændrer sig → browseren ser en ny SW.
//   2. updateViaCache:'none' → browseren henter /sw.js udenom HTTP-cachen ved
//      hver update-check, så en ny version altid opdages.
//   3. Vi kalder registration.update() ved load OG hver gang appen
//      foregrundes (visibilitychange) — dvs. "når man åbner den".
//   4. Den nye SW kalder skipWaiting()+clients.claim() (se sw.js), hvilket
//      udløser 'controllerchange'. Vi reloader ÉN gang, så den friske
//      index.html (no-cache) henter de nye hashede JS/CSS-filer.
if ('serviceWorker' in navigator) {
  // Reload præcis én gang når en NY service worker tager kontrol (= en
  // opdatering blev aktiveret). Springer den første install over (ingen
  // tidligere controller) for at undgå et unødigt reload på første besøg.
  let reloading = false;
  const hadControllerAtStart = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading || !hadControllerAtStart) return;
    reloading = true;
    window.location.reload();
  });

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      });
      console.log('[App] Service Worker registered:', registration.scope);

      // Tjek for en nyere SW hver gang appen åbnes/foregrundes. På iOS-PWA
      // er det her opdateringer reelt opdages efter appen har ligget i
      // baggrunden.
      const checkForUpdate = () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => { /* offline o.l. — ignorér */ });
        }
      };
      document.addEventListener('visibilitychange', checkForUpdate);

      // Hvis en opdatering allerede ventede ved registrering, promovér den nu.
      if (registration.waiting && navigator.serviceWorker.controller) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      // Når en ny SW findes: bed den springe waiting-fasen over, så den
      // aktiverer med det samme (controllerchange → reload ovenfor).
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('[App] Ny version installeret — aktiverer');
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });
    } catch (error) {
      console.error('[App] Service Worker registration failed:', error);
      captureError(error, { tags: { phase: 'sw-register' } });
    }
  });

  // Listen for messages from SW
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data.type === 'NAVIGATE' && event.data.url) {
      window.location.href = event.data.url;
    }
  });
}

// Supabase implicit-flow auth redirects (password recovery, OAuth sign-in, email
// confirmation) deliver their tokens in the URL *fragment*
// (#access_token=...&type=recovery). HashRouter ALSO owns the fragment, so it would
// route the token blob to /:slug → ListPage → "List not found" ("Liste ikke fundet")
// — and clobber the hash before supabase-js can read it, so the recovery session is
// never established and the reset UI never shows.
//
// Fix: when the landing URL looks like a Supabase auth redirect, block the first
// React render until supabase-js has parsed the fragment/query, established the
// session, fired PASSWORD_RECOVERY and stripped the token from the URL. Then
// HashRouter boots on a clean hash and App.tsx shows the reset UI via its
// PASSWORD_RECOVERY override. Awaiting getSession() awaits the client's internal
// initialize promise, which performs detectSessionInUrl. Keeping the implicit flow
// (rather than switching to PKCE) preserves cross-device email links — PKCE stores
// the verifier in the initiating browser's localStorage and breaks them.
function landedOnAuthRedirect(): boolean {
  const hash = window.location.hash;
  const search = window.location.search;
  return (
    /[#&]access_token=/.test(hash) ||
    /[#&](error|error_code|error_description)=/.test(hash) ||
    /[?&]code=/.test(search)
  );
}

async function bootstrap() {
  if (landedOnAuthRedirect()) {
    const auth = container.auth;
    if (auth instanceof SupabaseAuthProvider) {
      try {
        // Awaits the client's initialize promise → detectSessionInUrl runs,
        // consumes the fragment/query, establishes the session and cleans the URL.
        await auth.getClient().auth.getSession();
      } catch (err) {
        captureError(err, { tags: { phase: 'auth-url-bootstrap' } });
      }
    }
    // Defensive: error payloads (#error=...) may be left in the fragment; strip them
    // so HashRouter doesn't mis-route them. Success payloads are cleaned by
    // supabase-js itself.
    if (/[#&](access_token|error|error_code)=/.test(window.location.hash)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
